import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import ProtectedRoute from "../features/auth/AuthGuard";
import { PageTransition } from "../components/PageTransition";
import { DEFAULT_MODEL, getModelById } from "../config/models";

const MainContent = lazy(() => import("../pages/DashboardPage/MainContent"));
const ChatPage    = lazy(() => import("../pages/ChatPage"));
const LoginPage   = lazy(() => import("../pages/LoginPage"));
const SignUpPage  = lazy(() => import("../pages/SignUpPage"));
const LandingPage = lazy(() => import("../pages/LandingPage"));
const Sidebar     = lazy(() => import("../layouts/Sidebar"));
const SubscriptionPage = lazy(() => import("../pages/SubscriptionPage"));
const ProfilePage = lazy(() => import("../pages/ProfilePage"));
const AdminPage = lazy(() => import("../pages/AdminPage"));
const LegalPage = lazy(() => import("../pages/legal/LegalPage"));

const LoadingSpinner = () => null;

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem("isLoggedIn") === "true");
  const [userRole, setUserRole]     = useState(() => localStorage.getItem("userRole") || "user");
  const [selectedEngine, setSelectedEngine] = useState(() => localStorage.getItem("selectedEngine") || "11");
  const [isSidebarOpen, setIsSidebarOpen]   = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedModel, setSelectedModel] = useState(() => {
    const saved = localStorage.getItem("selectedModelId");
    return saved ? (getModelById(saved) || DEFAULT_MODEL) : DEFAULT_MODEL;
  });
  const sidebarRef = useRef(null);

  useEffect(() => { localStorage.setItem("selectedEngine", selectedEngine); }, [selectedEngine]);
  useEffect(() => { localStorage.setItem("isLoggedIn", isLoggedIn); }, [isLoggedIn]);
  useEffect(() => { localStorage.setItem("userRole", userRole); }, [userRole]);
  useEffect(() => { localStorage.setItem("selectedModelId", selectedModel?.id); }, [selectedModel]);

  const handleStartChat = (message) => {
    const conversationId = `${selectedEngine}_${Date.now()}`;
    localStorage.setItem("pendingMessage", message);
    localStorage.setItem("pendingConversationId", conversationId);
    navigate(`/chat/${conversationId}`, { state: { initialMessage: message } });
  };

  const handleLogout = async () => {
    try {
      const authService = (await import("../features/auth/api")).default;
      await authService.signOut();
    } catch { /* ignore */ }
    setIsLoggedIn(false);
    setUserRole("user");
    ["isLoggedIn","userRole","selectedEngine","userInfo","authToken","idToken","refreshToken"]
      .forEach((k) => localStorage.removeItem(k));
    window.dispatchEvent(new CustomEvent("userInfoUpdated"));
    navigate("/");
  };

  const handleLogin = (role = "user") => {
    setIsLoggedIn(true);
    setUserRole(role);
    navigate("/home");
  };

  const handleSelectEngine = (engine) => {
    setSelectedEngine(engine);
    if (isLoggedIn) {
      navigate("/home");
    } else {
      navigate("/login", { state: { engine } });
    }
  };

  const handleSignUp = () => {
    setIsLoggedIn(true);
    navigate("/home");
  };

  const handleNewConversation = () => {
    sidebarRef.current?.loadConversations?.();
  };

  const showSidebar = !["/", "/login", "/signup", "/subscription", "/profile", "/admin", "/terms", "/refund-policy", "/privacy", "/disclaimer"].includes(location.pathname) && !location.pathname.startsWith("/admin");

  return (
    <div
      className="flex w-full overflow-x-clip"
      style={{ minHeight: "100dvh", backgroundColor: "hsl(var(--bg-100))", color: "hsl(var(--text-100))" }}
    >
      {showSidebar && (
        <Sidebar
          ref={sidebarRef}
          selectedEngine={selectedEngine}
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen((p) => !p)}
          onCollapseChange={setIsSidebarCollapsed}
          onEngineChange={handleSelectEngine}
        />
      )}

      <motion.div
        className="min-h-full w-full min-w-0 flex-1"
        animate={{
          marginLeft: showSidebar && window.innerWidth >= 768
            ? (isSidebarCollapsed ? 52 : 260)
            : 0,
        }}
        transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
      >
        <AnimatePresence mode="wait">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes
              location={location}
              key={location.pathname.split("/").slice(0, 2).join("/")}
            >
              <Route
                path="/"
                element={
                  <PageTransition pageKey="landing">
                    <LandingPage
                      onSelectEngine={handleSelectEngine}
                      onLogin={handleLogin}
                      onLogout={handleLogout}
                    />
                  </PageTransition>
                }
              />
              <Route path="/login" element={<LoginPage onLogin={handleLogin} onGoToSignUp={() => navigate("/signup")} selectedEngine={location.state?.engine || selectedEngine} />} />
              <Route path="/signup" element={<SignUpPage onSignUp={handleSignUp} onBackToLogin={() => navigate("/login")} />} />

              {/* 메인 홈 */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <PageTransition pageKey="home">
                      <MainContent
                        userRole={userRole}
                        selectedEngine={selectedEngine}
                        onStartChat={handleStartChat}
                        onEngineChange={handleSelectEngine}
                        onToggleSidebar={() => setIsSidebarOpen((p) => !p)}
                        isSidebarOpen={isSidebarOpen}
                        selectedModel={selectedModel}
                        onModelChange={setSelectedModel}
                      />
                    </PageTransition>
                  </ProtectedRoute>
                }
              />

              {/* 채팅 */}
              <Route
                path="/chat/:conversationId?"
                element={
                  <ProtectedRoute>
                    <PageTransition pageKey="chat">
                      <ChatPage
                        initialMessage={location.state?.initialMessage}
                        userRole={userRole}
                        selectedEngine={selectedEngine}
                        onLogout={handleLogout}
                        onBackToLanding={() => navigate("/")}
                        onToggleSidebar={() => setIsSidebarOpen((p) => !p)}
                        isSidebarOpen={isSidebarOpen}
                        onNewConversation={handleNewConversation}
                        selectedModel={selectedModel}
                        onModelChange={setSelectedModel}
                      />
                    </PageTransition>
                  </ProtectedRoute>
                }
              />

              <Route path="/subscription" element={<ProtectedRoute><PageTransition pageKey="subscription"><SubscriptionPage /></PageTransition></ProtectedRoute>} />
              <Route path="/profile"      element={<ProtectedRoute><PageTransition pageKey="profile"><ProfilePage /></PageTransition></ProtectedRoute>} />
              <Route path="/admin"        element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />

              <Route path="/terms"        element={<LegalPage type="terms" />} />
              <Route path="/refund-policy" element={<LegalPage type="refund" />} />
              <Route path="/privacy"      element={<LegalPage type="privacy" />} />
              <Route path="/disclaimer"   element={<LegalPage type="disclaimer" />} />

              {/* 기존 /11, /22 경로 리다이렉트 */}
              <Route path="/11/*" element={<Navigate to="/home" replace />} />
              <Route path="/22/*" element={<Navigate to="/home" replace />} />
              <Route path="*"     element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
